import { Building2, ChevronRight, Plus } from "lucide-react";
import { useEffect, useRef, type JSX } from "react";
import { Link, useSearchParams } from "react-router";

import { LoadingState } from "../components/data/LoadingState.js";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card.js";
import { useSwitchTenantMutation } from "../lib/api/auth.js";
import { useMyCompaniesQuery, type CompanyMembership, type UserRole } from "../lib/api/users.js";
import { APIError } from "../lib/http.js";
import { enterApp } from "../lib/tenant-url.js";
import { useAuth } from "../providers/auth-provider.js";

const COMPANY_CHOSEN_KEY = "onshelf.companyChosen";
const SELECTED_STORE_KEY = "onshelf.selectedStoreId";

const ROLE_LABELS: Record<UserRole, string> = {
  tenant_admin: "Admin",
  ops_manager: "Ops Manager",
  store_manager: "Store Manager",
  store_associate: "Store Associate",
  field_auditor: "Field Auditor",
  viewer: "Viewer",
};

function setChosenFlag(): void {
  try {
    localStorage.setItem(COMPANY_CHOSEN_KEY, "1");
  } catch {
    /* ignore disabled storage */
  }
}

function resetSelectedStore(): void {
  try {
    localStorage.removeItem(SELECTED_STORE_KEY);
  } catch {
    /* ignore disabled storage */
  }
}

export default function SelectCompanyPage(): JSX.Element {
  const { user, logout } = useAuth();
  const companiesQuery = useMyCompaniesQuery();
  const switchMut = useSwitchTenantMutation();
  const autoRedirected = useRef(false);
  const [searchParams] = useSearchParams();

  const companies = companiesQuery.data?.items ?? [];

  // When the user reached this page by explicitly choosing "Switch company"
  // (?switch=1), always show the picker — never auto-bounce. Otherwise a
  // single-company user could never re-open this page to confirm/switch.
  const explicitSwitch = searchParams.get("switch") === "1";

  // Defensive: a single-company user never needs to choose — flag + enter the
  // app (full navigation into /t/<code>, since this page is on the public
  // router and the app lives under the tenant basename). Skipped on an
  // explicit switch so the user stays on the picker.
  useEffect(() => {
    if (explicitSwitch) return;
    if (!companiesQuery.isSuccess || autoRedirected.current) return;
    if (companies.length === 0) {
      autoRedirected.current = true;
      window.location.replace("/onboarding");
      return;
    }
    if (companies.length === 1 && user?.tenantCode) {
      autoRedirected.current = true;
      setChosenFlag();
      enterApp(user.tenantCode);
    }
  }, [explicitSwitch, companies.length, companiesQuery.isSuccess, user?.tenantCode]);

  const handleSelect = async (company: CompanyMembership): Promise<void> => {
    // See AppTopbar's handleSwitch comment — the mutation's onSuccess
    // already updates qk.auth.me + removes non-auth queries. The switch
    // response carries the new tenant's code, which we use to enter its app.
    const res = await switchMut.mutateAsync({ tenantId: company.tenantId });
    setChosenFlag();
    resetSelectedStore();
    enterApp(res.user.tenantCode);
  };

  const switchError = switchMut.error
    ? switchMut.error instanceof APIError
      ? switchMut.error.displayDetail
      : (switchMut.error as Error).message
    : null;

  return (
    <main className="relative flex h-full items-center justify-center bg-background p-6">
      <div className="absolute right-4 top-4 flex items-center gap-3">
        {user?.email ? (
          <span className="hidden text-xs text-muted-foreground sm:inline">{user.email}</span>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => void logout()}>
          Sign out
        </Button>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Choose a company</CardTitle>
          <CardDescription>Select which company you want to work in.</CardDescription>
        </CardHeader>
        <CardContent>
          {companiesQuery.isLoading ? (
            <LoadingState variant="spinner" message="Loading companies…" />
          ) : companiesQuery.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load companies</AlertTitle>
              <AlertDescription>
                {companiesQuery.error instanceof APIError
                  ? companiesQuery.error.displayDetail
                  : "Please try again."}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {switchError ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not switch company</AlertTitle>
                  <AlertDescription>{switchError}</AlertDescription>
                </Alert>
              ) : null}
              <ul className="space-y-2">
                {companies.map((company) => (
                  <li key={company.tenantId}>
                    <button
                      type="button"
                      disabled={switchMut.isPending}
                      onClick={() => void handleSelect(company)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Building2 className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {company.tenantName}
                        </span>
                        <span className="mt-0.5 inline-flex">
                          <Badge variant="neutral">
                            {ROLE_LABELS[company.role] ?? company.role}
                          </Badge>
                        </span>
                      </span>
                      <ChevronRight
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border pt-4">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/onboarding">
                    <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                    Create a new company
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
